import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ErrorBoundary from '../components/ui/ErrorBoundary';
import FieldError from '../components/ui/FieldError';
import { INDIA_STATES } from '../constants/indiaStates';
import { COUNTRY_CODES } from '../constants/countryCodes';

import Loading from '../components/ui/Loading';

const ShopDetails = () => {
  const { user, updateUser } = useAuth();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    shopName: '',
    shopDescription: '',
    shopType: '',
    customCategory: '',
    businessLicense: '',
    gstNumber: '',
    panNumber: '',
    establishmentYear: '',
    website: '',
    socialMedia: {
      facebook: '',
      instagram: '',
      twitter: ''
    },
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'India',
    operatingHours: {
      monday: { open: '09:00', close: '18:00', closed: false },
      tuesday: { open: '09:00', close: '18:00', closed: false },
      wednesday: { open: '09:00', close: '18:00', closed: false },
      thursday: { open: '09:00', close: '18:00', closed: false },
      friday: { open: '09:00', close: '18:00', closed: false },
      saturday: { open: '09:00', close: '18:00', closed: false },
      sunday: { open: '09:00', close: '18:00', closed: true }
    }
  });

  // Prefill data from context if exists
  useEffect(() => {
    if (user?.shopDetails) {
      const shopDetails = user.shopDetails;
      
      // Handle case where shopType is not one of the predefined options
      const predefinedTypes = ['fashion', 'electronics', 'home', 'beauty', 'sports', 'books', 'toys', 'art'];
      const isCustomType = shopDetails.shopType && !predefinedTypes.includes(shopDetails.shopType);
      
      setFormData(prev => ({
        ...prev,
        ...shopDetails,
        shopType: isCustomType ? 'other' : shopDetails.shopType,
        customCategory: isCustomType ? shopDetails.shopType : ''
      }));
    }
  }, [user?.shopDetails]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Ensure ZIP code only accepts numbers
    let processedValue = value;
    if (name === 'zipCode') {
      // Only allow numeric characters
      processedValue = value.replace(/[^0-9]/g, '');
    }
    
    // Limit shop description to 500 characters
    if (name === 'shopDescription') {
      processedValue = value.slice(0, 500);
    }
    
    // Clear custom category if user selects a different option from "other"
    if (name === 'shopType' && processedValue !== 'other') {
      setFormData(prev => ({
        ...prev,
        [name]: processedValue,
        customCategory: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: processedValue
      }));
    }
    setError('');
  };

  const handleSocialMediaChange = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      socialMedia: {
        ...prev.socialMedia,
        [platform]: value
      }
    }));
  };

  const handleOperatingHoursChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [day]: {
          ...prev.operatingHours[day],
          [field]: value
        }
      }
    }));
  };

  const toggleDayClosed = (day) => {
    setFormData(prev => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [day]: {
          ...prev.operatingHours[day],
          closed: !prev.operatingHours[day].closed
        }
      }
    }));
  };

  const validateForm = () => {
    if (!formData.shopName.trim()) {
      setError('Shop name is required');
      return false;
    }
    if (!formData.shopDescription.trim()) {
      setError('Shop description is required');
      return false;
    }
    if (formData.shopDescription.length > 500) {
      setError('Shop description must be 500 characters or less');
      return false;
    }
    if (!formData.shopType.trim()) {
      setError('Business category is required');
      return false;
    }
    if (formData.shopType === 'other' && !formData.customCategory.trim()) {
      setError('Please specify your business category');
      return false;
    }
    // Validate business address fields
    if (!formData.addressLine1.trim()) {
      setError('Street address is required');
      return false;
    }
    if (!formData.city.trim()) {
      setError('City is required');
      return false;
    }
    if (!formData.state.trim()) {
      setError('State is required');
      return false;
    }
    if (!formData.zipCode || !formData.zipCode.toString().trim()) {
      setError('PIN code is required');
      return false;
    }
    return true;
  };

  const handleContinue = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setError('');

    try {
      // Prepare data for submission - use custom category if "other" is selected
      const shopDetailsToSave = {
        ...formData,
        shopType: formData.shopType === 'other' ? formData.customCategory : formData.shopType,
        country: 'India' // Explicitly set country to India
      };

      // Update user data with shop details
      updateUser({
        shopDetails: shopDetailsToSave
      });

      setSuccess('Shop details saved successfully!');

      // Navigate to review page
      setTimeout(() => {
        navigate('/review-submit');
      }, 1000);

    } catch (err) {
      setError('Failed to save shop details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/representative-details');
  };

  return (
    <ErrorBoundary>
      <div className="form-container">
        <div className="form-content">
          {/* Progress Indicator */}
          <div className="progress-indicator">
            <div className="progress-step completed">
              <div className="step-number">1</div>
              <div className="step-label">Business Type</div>
            </div>
            <div className="progress-line completed"></div>
            <div className="progress-step completed">
              <div className="step-number">2</div>
              <div className="step-label">Representative</div>
            </div>
            <div className="progress-line completed"></div>
            <div className="progress-step active">
              <div className="step-number">3</div>
              <div className="step-label">Shop Details</div>
            </div>
            <div className="progress-line"></div>
            <div className="progress-step">
              <div className="step-number">4</div>
              <div className="step-label">Review</div>
            </div>
          </div>

          <div className="form-header">
            <h1 className="form-title">Shop Details</h1>
            <p className="form-subtitle">
              Provide detailed information about your business
            </p>
          </div>

          <div className="form-body">
            <div className="form-sections">
              {/* Basic Information */}
              <div className="form-section">
                <h3 className="section-title">Basic Information</h3>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label htmlFor="shopName" className="form-label">Shop/Business Name *</label>
                    <input
                      type="text"
                      id="shopName"
                      name="shopName"
                      value={formData.shopName}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Enter your shop/business name"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label htmlFor="shopDescription" className="form-label">Business Description *</label>
                    <textarea
                      id="shopDescription"
                      name="shopDescription"
                      value={formData.shopDescription}
                      onChange={handleInputChange}
                      className="form-textarea"
                      placeholder="Describe your business, products/services offered"
                      rows="4"
                      maxLength="500"
                    />
                    <div className={`character-counter ${formData.shopDescription.length > 450 ? 'warning' : ''}`}>
                      {formData.shopDescription.length}/500 characters
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="shopType" className="form-label">Business Category *</label>
                    <select
                      id="shopType"
                      name="shopType"
                      value={formData.shopType}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="">Select category</option>
                      <option value="fashion">Fashion & Apparel</option>
                      <option value="electronics">Electronics & Gadgets</option>
                      <option value="home">Home & Garden</option>
                      <option value="beauty">Beauty & Health</option>
                      <option value="sports">Sports & Outdoors</option>
                      <option value="books">Books & Media</option>
                      <option value="toys">Toys & Games</option>
                      <option value="art">Art & Crafts</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Custom Category Input - Only show when "Other" is selected */}
                  {formData.shopType === 'other' && (
                    <div className="form-group">
                      <label htmlFor="customCategory" className="form-label">Please specify your business category *</label>
                      <input
                        type="text"
                        id="customCategory"
                        name="customCategory"
                        value={formData.customCategory}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="Enter your business category"
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="establishmentYear" className="form-label">Year of Establishment</label>
                    <input
                      type="number"
                      id="establishmentYear"
                      name="establishmentYear"
                      value={formData.establishmentYear}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="e.g., 2020"
                      min="1900"
                      max={new Date().getFullYear()}
                    />
                  </div>
                </div>
              </div>

              {/* Business Address */}
              <div className="form-section">
                <h3 className="section-title">Business Address</h3>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label htmlFor="addressLine1" className="form-label">Street address *</label>
                    <input
                      type="text"
                      id="addressLine1"
                      name="addressLine1"
                      value={formData.addressLine1}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Street address"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label htmlFor="addressLine2" className="form-label">Apartment, suite, building, etc. (optional)</label>
                    <input
                      type="text"
                      id="addressLine2"
                      name="addressLine2"
                      value={formData.addressLine2}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Apartment, suite, building, etc."
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="city" className="form-label">City *</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="City"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="state" className="form-label">State *</label>
                    <select
                      id="state"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="">Select a state</option>
                      {INDIA_STATES.map((state) => (
                        <option key={state.value} value={state.value}>
                          {state.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="zipCode" className="form-label">PIN code *</label>
                    <input
                      type="text"
                      id="zipCode"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleInputChange}
                      className="form-input zipcode-input"
                      placeholder="PIN code"
                      maxLength="6"
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="country" className="form-label">Country</label>
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      className="form-select"
                    >
                      <option value="India">India</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                onClick={handleBack}
                className="btn btn-outline back-btn"
                disabled={isLoading}
              >
                ← Back
              </button>

              <FieldError error={error} />


              <button
                onClick={handleContinue}
                disabled={isLoading}
                className={`btn btn-primary continue-btn ${isLoading ? 'loading' : ''}`}
              >
                {isLoading ? <Loading /> : 'Continue to Review'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default ShopDetails;
